import { FC } from "react";
import * as _ from "lodash";

import { Dot, Icon, Smile, Steps, Typography } from "@ctra/components";
import { InsightValidation as ValidationValues, InsightWorkflowState, ValidationLookup } from "@ctra/api";
import { Enterprise, useTranslation } from "@ctra/i18n";

import {
  ResolutionTagList,
  ResolutionWidget,
  useInsightResolutions,
  useInsightValidation,
  ValidationButtons,
  ValidationQuestion
} from "@insights";

import styles from "./AjinomotoTwoStepValidation.module.less";

const { Step } = Steps;
const { Text } = Typography;

/**
 * 2-step generic validation for Ajinomoto insights
 * @return {React.ReactNode}
 * @constructor
 */
export const AjinomotoTwoStepValidation: FC = () => {
  const { t } = useTranslation();
  const { validation: validationEntity, validate } = useInsightValidation();

  const {
    resolutions,
    unresolve,
    meta: { isResolved }
  } = useInsightResolutions();

  const {
    insightValidations: { validationValue }
  } = Enterprise;

  const { validation } = validationEntity;

  /**
   * Get current step based on the validation value
   */
  const getCurrentStep = () => {
    switch (validation) {
      case ValidationValues.na: {
        return 0;
      }
      case ValidationValues.no:
      case ValidationValues.yes: {
        return isResolved ? 2 : 1;
      }
    }
  };

  /**
   * Handle validation attempt and make sure the unresolve doesn't update workflow state
   * because its already being updated via the validation.
   * @param value
   */
  const onValidate = _.flow(validate, _.partial(unresolve, { updatesWorkflowState: false }));

  return (
    <Steps current={getCurrentStep()} className={styles.Steps} direction="vertical">
      <Step
        icon={<Icon component={Smile} className={styles.Smile} />}
        title={<ValidationQuestion highlighted={validation === ValidationValues.na} step={1} />}
        description={
          validation === ValidationValues.na ? (
            <ValidationButtons
              options={[ValidationValues.yes, ValidationValues.no]}
              workflowStates={[InsightWorkflowState.done, InsightWorkflowState.done]}
            />
          ) : (
            <Text type="secondary" className={styles.Link}>
              {t<string>(validationValue[ValidationLookup[validation]])}
            </Text>
          )
        }
      />
      {validation !== ValidationValues.na && !!_.keys(resolutions).length && (
        <Step
          icon={<Icon component={Dot} />}
          title={
            <ValidationQuestion
              highlighted={!isResolved}
              rollback={isResolved ? void 0 : _.partial(onValidate, ValidationValues.na)}
              step={2}
              validation={validation}
            />
          }
          description={isResolved ? <ResolutionTagList includeValidation={false} /> : <ResolutionWidget />}
        />
      )}
      {validation !== ValidationValues.na && isResolved && (
        <Step
          status="process"
          icon={<Icon component={Dot} />}
          title={
            /**
             * If there are no resolutions for the selected validation,
             * then we just change the validation to NA, else we unresolve.
             * Using _.ary(unresolve, 0) instead of just unresolve
             * because rollback sends an unwanted event argument which needs to be avoided.
             */
            <ValidationQuestion
              highlighted
              rollback={
                _.isEmpty(resolutions) ? _.partial(onValidate, ValidationValues.na) : _.ary(unresolve, 0)
              }
              step={3}
              validation={validation}
            />
          }
        />
      )}
    </Steps>
  );
};
